import { useEffect, useRef, useState } from 'preact/hooks';
import { NAME_RE, AVATAR_COUNT, checkName, type NoticeView, type Visibility } from '@knight/shared';
import * as S from '../app/state';
import { t, tn, lang } from '../i18n';
import { Btn, Avatar } from './common';
import { api, setToken } from '../net/api';
import { game } from '../game/client';
import { renderGoogleButton } from './google';
import { PlayerLookup } from './Watch';

export function Profile() {
  const acc = S.account.value;
  const [name, setName] = useState(acc?.name ?? '');
  const [notices, setNotices] = useState<NoticeView[]>([]);
  const [confirmSwitch, setConfirmSwitch] = useState<{ idToken: string; other: { name: string; fame: number } } | null>(null);
  const gRef = useRef<HTMLDivElement>(null);
  const online = S.conn.value === 'online';

  useEffect(() => {
    if (online) api.notices().then(setNotices).catch(() => {});
  }, [online]);

  useEffect(() => {
    if (!gRef.current || !acc?.isGuest || !S.googleReady.value || !online) return;
    api.health().then((h) => {
      const cid = (h as unknown as { googleClientId?: string }).googleClientId;
      if (cid && gRef.current) void renderGoogleButton(gRef.current, cid, (idToken) => void onGoogle(idToken, true), lang.value);
    });
  }, [acc?.isGuest, online]);

  async function onGoogle(idToken: string, merge: boolean) {
    try {
      const r = await api.google(idToken, merge);
      if (r.conflict && r.other) {
        setConfirmSwitch({ idToken, other: r.other });
        return;
      }
      setToken(r.token);
      S.account.value = r.account;
      game.rehello();
      S.showToast(t('toast.saved'));
    } catch (e) {
      S.showToast(t('toast.error', { msg: (e as Error).message }));
    }
  }

  async function save() {
    if (!NAME_RE.test(name.trim())) {
      S.showToast(t('toast.nameBad'));
      return;
    }
    const verdict = checkName(name.trim());
    if (verdict !== 'ok') {
      S.showToast(t(verdict === 'reserved' ? 'toast.nameReserved' : 'toast.nameProfane'));
      return;
    }
    try {
      if (online) S.account.value = await api.updateMe({ name: name.trim() });
      else if (acc) S.account.value = { ...acc, name: name.trim() };
      localStorage.setItem('knight.name', name.trim());
      S.showToast(t('toast.saved'));
    } catch (e) {
      S.showToast(t('toast.error', { msg: (e as Error).message }));
    }
  }

  async function setAvatar(i: number) {
    try {
      if (online) S.account.value = await api.updateMe({ avatar: i });
      else if (acc) S.account.value = { ...acc, avatar: i };
    } catch (e) {
      S.showToast(t('toast.error', { msg: (e as Error).message }));
    }
  }

  async function setShow(patch: { visibility?: Visibility; showMode?: boolean }) {
    try {
      if (online) S.account.value = await api.updateMe(patch);
      else S.showToast(t('toast.needServer'));
    } catch (e) {
      S.showToast(t('toast.error', { msg: (e as Error).message }));
    }
  }

  if (!acc) return null;
  const st = acc.stats;
  return (
    <div class="screen">
      <div class="card wide panel col tall">
        <div class="row">
          <h2>{t('btn.profile')}</h2>
          <div class="grow" />
          <Btn small onClick={() => (S.screen.value = 'home')}>
            {t('btn.back')}
          </Btn>
        </div>
        <div class="row split" style={{ alignItems: 'flex-start', gap: 24 }}>
          <div class="col" style={{ flex: 1 }}>
            <label class="small muted">{t('label.name')}</label>
            <div class="row">
              <input class="text grow" value={name} maxLength={16} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
              <Btn onClick={() => void save()}>{t('btn.save')}</Btn>
            </div>
            <label class="small muted">{t('label.avatar')}</label>
            <div class="avatars">
              {Array.from({ length: AVATAR_COUNT }).map((_, i) => (
                <Avatar i={i} sel={acc.avatar === i} onClick={() => void setAvatar(i)} />
              ))}
            </div>
            <div class="sep" />
            <h3>{t('label.showMode')}</h3>
            <div class="setting">
              <span>
                {t('label.visibility')}
                <div class="small muted">{t('label.visibilityHint')}</div>
              </span>
              <select value={acc.visibility ?? 'public'} disabled={!online} onChange={(e) => void setShow({ visibility: (e.target as HTMLSelectElement).value as Visibility })}>
                {(['public', 'guild', 'hidden'] as Visibility[]).map((v) => (
                  <option value={v}>{t('label.vis.' + v)}</option>
                ))}
              </select>
            </div>
            <div class="setting">
              <span>
                {t('label.showModeToggle')}
                <div class="small muted">{t('label.showModeHint')}</div>
              </span>
              <label class="row clickable" style={{ justifyContent: 'flex-end' }}>
                <input type="checkbox" checked={!!acc.showMode} disabled={!online} onChange={(e) => void setShow({ showMode: (e.target as HTMLInputElement).checked })} />
              </label>
            </div>
            {online && (
              <div class="small muted">
                {t('label.showLink')}: <span class="gold">{location.origin + location.pathname}#/w/{encodeURIComponent(acc.name)}</span>
              </div>
            )}
            <div class="sep" />
            <h3>{t('label.findPlayerTitle')}</h3>
            <PlayerLookup />
            <div class="sep" />
            {acc.isGuest ? (
              <div class="col">
                <div class="small muted">{S.googleReady.value ? t('label.google.hint') : t('label.google.unavailable')}</div>
                <div ref={gRef} />
                {confirmSwitch && (
                  <div class="card col" style={{ padding: 10 }}>
                    <div class="small">{t('label.google.merge')}</div>
                    <div class="small muted">
                      {confirmSwitch.other.name} · {t('label.fame')} {confirmSwitch.other.fame}
                    </div>
                    <div class="row">
                      <Btn
                        primary
                        small
                        onClick={() => {
                          const tok = confirmSwitch.idToken;
                          setConfirmSwitch(null);
                          void onGoogle(tok, false);
                        }}
                      >
                        {t('btn.continue')}
                      </Btn>
                      <Btn small onClick={() => setConfirmSwitch(null)}>
                        {t('btn.cancel')}
                      </Btn>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div class="row">
                <span class="badge on">{t('label.google.linked')}</span>
                <Btn
                  small
                  onClick={async () => {
                    try {
                      await api.signout();
                    } catch {
                      /* ignore */
                    }
                    setToken(null);
                    location.reload();
                  }}
                >
                  {t('btn.signout')}
                </Btn>
              </div>
            )}
          </div>
          <div class="col" style={{ flex: 1 }}>
            <h3>{t('label.stats')}</h3>
            <div class="kv">
              <span>{t('label.fame')}</span>
              <span>{acc.fame}</span>
              <span>{t('label.rating')}</span>
              <span>{acc.rating}</span>
              <span>{t('label.kills')}</span>
              <span>{st.kills}</span>
              <span>{t('lb.bestLevel')}</span>
              <span>{st.bestLevel}</span>
              <span>{t('label.bossKills')}</span>
              <span>{st.bossKills}</span>
              <span>{t('label.revenantKills')}</span>
              <span>{st.revenantKills}</span>
              <span>{t('lb.rating')}</span>
              <span>
                {st.duelWins} / {st.duelLosses}
              </span>
              <span>{t('label.referral')}</span>
              <span>{st.referrals}</span>
            </div>
            {acc.titles.length > 0 && (
              <>
                <h3>{t('label.titles')}</h3>
                <div class="row wrap">
                  {acc.titles.map((tt) => (
                    <span class="badge on">{t('title.' + tt)}</span>
                  ))}
                </div>
              </>
            )}
            {notices.length > 0 && (
              <>
                <h3>{t('label.recentNotices')}</h3>
                <div class="list small">
                  {notices.slice(0, 8).map((n) => (
                    <div class="item">{t(n.key, Object.fromEntries(Object.entries(n.vars).map(([k, v]) => [k, k === 'ghost' || k === 'name' || k === 'killer' ? String(v) : v])))}</div>
                  ))}
                </div>
              </>
            )}
            {acc.favoriteHero && (
              <div class="small muted">
                {t('btn.chooseHero')}: {tn('hero', acc.favoriteHero)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
